import { questionOpener, trackQuestionOpened, type QuestionRole } from '@src/utils/questionAnalytics';
import { QuestionIcon } from '@src/components/icons/QuestionIcon';
import { ThemeIcon } from '@src/theme/ThemeIcon';
import { Trans, useTranslation } from 'react-i18next';
import { Columns, Expandable, DistributionMethodCard, LinkIcon, MailIcon, UsersIcon, Typography, Stack, Link, Hint, showDialog } from '@src/components';
import './InvitationGuide.css';

interface InvitationGuideProps {
  role: QuestionRole;
  onShared?: () => void;
}

export function InvitationGuide({ onShared, role }: InvitationGuideProps) {
  const { t } = useTranslation();
  const accessHelp = (method: 'email' | 'personal' | 'shared') => (
          <section data-testid="personal-link-access-help">
            <Stack gap={3}>
              {method === 'email' && (['account', 'existingAccount'] as const).map(part => (
                <Typography as="p" role="body/sm" key={part}>{t(`game.invitationGuide.help.emailAccess.${part}`)}</Typography>
              ))}
              {(['browser', 'device'] as const).map(part => (
                <Typography as="p" role="body/sm" color="inherit" key={part}>{t(`game.invitationGuide.help.${method === 'shared' ? 'sharedLinkAccess' : 'personalLinkAccess'}.${part}`)}</Typography>
              ))}
              <Hint tone="warning">
                <Typography as="p" role="body/sm" color="inherit">{t(`game.invitationGuide.help.${method === 'shared' ? 'sharedLinkAccess' : method === 'email' ? 'emailAccess' : 'personalLinkAccess'}.warning`)}</Typography>
              </Hint>
            </Stack>
          </section>
  );
  const help = (method: 'email' | 'personal' | 'shared') => (
    <div className="invitation-guide__help">
      <QuestionIcon aria-hidden="true" />
      <Link variant="inline" testId={`game-method-${method}-help`} onClick={questionOpener(method === 'email' ? 'invite_email' : method === 'personal' ? 'invite_personal_links' : 'invite_shared_link', role, () => showDialog({
        title: t(method === 'shared' ? 'game.invitationGuide.help.sharedLinkAccess.title' : method === 'email' ? 'game.invitationGuide.help.emailAccess.title' : 'game.invitationGuide.help.personalLinkAccess.title'),
        closeLabel: t('dialog.close'),
        submit: t('game.invitationGuide.help.close'),
        testId: `game-method-${method}-help-dialog`,
        children: <Stack gap={4}>
          {accessHelp(method)}
          <Typography as="h3" role="title/card">{t(`game.invitationGuide.help.${method}.usageTitle`)}</Typography>
          {method !== 'shared' && <Typography as="p" role="body/sm">{t(`game.invitationGuide.help.${method}.intro`)}</Typography>}
          <ol className="invitation-guide__steps">{Array.from({ length: method === 'shared' ? 3 : 5 }, (_, index) =>
            <Typography as="li" role="body/sm" key={index}><Trans t={t} i18nKey={`game.invitationGuide.help.${method}.steps.${index}`} components={{ mail: <span className="invitation-guide__inline-icon"><ThemeIcon name="mail" fallback={<MailIcon />} /></span> }} /></Typography>
          )}</ol>
          {method === 'shared' && <Typography as="p" role="body/sm">{t('game.invitationGuide.help.shared.recovery')}</Typography>}
        </Stack>,
      }))}>{t('game.invitationGuide.help.label')}</Link>
    </div>
  );
  return (
    <section className="invitation-guide landscape-sky-content" data-testid="game-invitation-guide">
      <h2 className="invitation-guide__title type type--title-section">
        {t('game.invitationGuide.title')}
      </h2>
      <div className="invitation-guide__body">
        <Typography as="h3" role="title/card" className="invitation-guide__lead">{t('game.invitationGuide.lead')}</Typography>
        <Columns columns={3} gap={4}>
          <DistributionMethodCard
            surface="outlined"
            icon={<MailIcon />}
            tone="success"
            title={t('box.organizer.methods.email.title')}
            badge={t('box.organizer.methods.email.badge')}
            body={t('game.invitationGuide.email')}
            points={[0, 1, 2].map(index => ({ text: t(`game.invitationGuide.emailPoints.${index}`), neutral: index === 2 }))}
            hint={t('box.organizer.methods.email.hint')}
            help={help('email')}
            testId="game-method-email"
          />
          <DistributionMethodCard
            surface="outlined"
            icon={<LinkIcon />}
            tone="support"
            title={t('box.organizer.methods.personal.title')}
            body={t('game.invitationGuide.personal')}
            points={[0, 1, 2].map(index => t(`game.invitationGuide.personalPoints.${index}`))}
            hint={t('box.organizer.methods.personal.hint')}
            help={help('personal')}
            testId="game-method-personal"
          />
          <DistributionMethodCard
            surface="outlined"
            icon={<UsersIcon />}
            tone="warning"
            title={t('box.organizer.methods.shared.title')}
            body={t('game.invitationGuide.shared')}
            points={[0, 1, 2].map(index => t(`game.invitationGuide.sharedPoints.${index}`))}
            action={
              onShared ? { label: t('box.organizer.sharedInvite'), onClick: onShared } : undefined
            }
            help={help('shared')}
            testId="game-method-shared"
          />
        </Columns>
        <Expandable
          elevated={false}
          className="invitation-guide__troubleshooting"
          testId="game-invitation-troubleshooting"
          onOpenChange={open => { if (open) trackQuestionOpened('participant_access_troubleshooting', role); }}
          title={t('game.invitationGuide.troubleshooting.title')}
        >
          <Stack gap={3}>
            <Typography as="p" role="body/sm">{t('game.invitationGuide.troubleshooting.intro')}</Typography>
            <Typography as="p" role="body/sm">{t('game.invitationGuide.troubleshooting.recoveryIntro')}</Typography>
            <ol className="invitation-guide__steps">{[0, 1, 2].map(index => (
              <Typography as="li" role="body/sm" key={index}>{t(`game.invitationGuide.troubleshooting.steps.${index}`)}</Typography>
            ))}</ol>
            <Typography as="h4" role="body/strong">{t('game.invitationGuide.troubleshooting.aliasTitle')}</Typography>
            <Typography as="p" role="body/sm"><Trans t={t} i18nKey="game.invitationGuide.troubleshooting.alias" components={{ strong: <strong /> }} /></Typography>
            <Typography as="p" role="body/sm">{t('game.invitationGuide.troubleshooting.aliasHint')}</Typography>
          </Stack>
        </Expandable>
      </div>
    </section>
  );
}
